import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource} from '@api'
import {MenuDivider} from '@blueprintjs/core'
import {Tooltip} from '@material-ui/core'
import {AssortmentCardStore, AssortmentHeroCardStoreContext, BuilderCardStore, CatalogFieldValueContextMenuItem, ICatalogItemDetailsPathElement, IContextMenuItems, IContextualHoverItemStore, useAssortmentCardStore, useVipLocalStore, VipLocalStore} from '@modules'
import {ImageRecord, StoresInstance, ui} from '@stores'
import {MyImage} from 'centric-ui-components'
import {autorun, runInAction, toJS} from 'mobx'
import * as css from './AssortmentSwatch.css'

/// region Store

export class AssortmentSwatchStore extends VipLocalStore implements IContextMenuItems, IContextualHoverItemStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { card: AssortmentCardStore }) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {catalog: {settings: {assortment: {swatches: {globalSelected}}}}} = this

        if (globalSelected) {
          runInAction(() => {
            // console.log(this.swatch)
            if (globalSelected && this.swatch?.item?.name == globalSelected) {
              this.card.selectedSwatchStore = this
            }
          })
        }
      })
    ]

    // if (this.props.index === 0 && !this.card.selectedSwatch) {
    //   this.card.selectedSwatch = this
    // }

    this.card.componentStores.swatches.storesById[this.id] = this
    return () => {
      delete this.card.componentStores.swatches.storesById[this.id]

      _toDispose.forEach(d => d())
    }
  }

  get data() {
    return this.props.card.bucket.swatches
  }

  get card() { return this.props.card}

  get config() {
    if (!this.swatch) return null
    return (this.card?.assortment?.swatches ?? []).find(f => f.field === this.swatch.field)
  }

  get swatch() { return this.props.swatch}

  @computed get id() { return this.swatch ? this.catalog.idFor(this.swatch.item, false) : null}

  @computed get images() {
    if (!this.swatch) return []
    const {swatch: {item}, config: {dataMap}} = this
    const r = _.get(item, dataMap.images as string ?? 'images')

    return _.isString(r) ? [{exId: r}] : r
  }

  @computed get image() : ImageRecord {
    const {images} = this
    return _.isArray(images) ? _.first(this.images) : images
  }

  @computed get imageUrl() {
    const {catalog, image} = this
    return catalog.imageUrlFor(image, {c8Size: 'image'})
  }

  @computed get title() {
    if (!this.swatch) return null

    const {config: {dataMap}, swatch: {item}, strings} = this

    let v = _.get(item, dataMap?.title?.field ?? 'name')
    return (dataMap.title?.render ? dataMap.title.render(v, this.stores) : v) || strings.emptyLabel
  }

  @computed get foreignKeyField() {
    if (!this.swatch) return null
    const {config: {field}, catalog: {findFieldInTree}} = this
    return findFieldInTree(field, true)
  }

  get contextMenuItems() {
    const {foreignKeyField, swatch} = this
    return <AssortmentSwatchStoreContext.Provider value={this}>
      {foreignKeyField && swatch && <CatalogFieldValueContextMenuItem field={foreignKeyField} value={swatch.item}/>}
      <MenuDivider/>

      {this.card.contextMenuItems}
    </AssortmentSwatchStoreContext.Provider>
  }

  @action onContextMenu = e => {
    this.contextMenu.onComponentContextMenu(e, this)
  }

  @action onClick = () => this.card.selectedSwatchStore = this.isSelected ? null : this

  @computed get isSelected() { return this.card.selectedSwatchStore === this}

  @computed get detailsPath(): ICatalogItemDetailsPathElement[] {
    const {foreignKeyField, catalog: {idFor}, catalog, swatch} = this
    if (!swatch || foreignKeyField === undefined) return []

    return [
      {
        where: [{value: idFor(swatch?.item, false)}],
        esIndex: foreignKeyField?.esIndex ?? catalog?.index,
        catalogField: foreignKeyField?.field
      }
    ]
  }

  @computed get associatedWorkspace() {
    const {catalog, foreignKeyField} = this
    return foreignKeyField ? catalog.workspaceForField(foreignKeyField) : catalog.workspace
  }

  @action onDoubleClick = (e: React.MouseEvent) => {
    return // turn off
    e.stopPropagation()

    const {stores: {flexLayoutCatalogRoot: {tab, tab: {openCatalogItemDetailsInNewTab}}}} = this,
      {detailsPath, associatedWorkspace} = this

    !_.isEmpty(detailsPath) && associatedWorkspace && openCatalogItemDetailsInNewTab({
      path: detailsPath,
      workspace: associatedWorkspace._id,
      parentTab: tab.id
    })

    setTimeout(() => {
      this.card.scrollIntoView()
    }, ui.settings.flexLayout.scrollIntoViewDelayAfterNewChildTab)
  }

  @computed get hoverItemEvents() {
    return this.card.hoverItemEventsFor(this)
  }

  @computed get hoverId() { return this.id}

  @computed get catalogField() {
    const {catalog: {findFieldInTree}, config} = this
    return findFieldInTree(config?.field)
  }

  matchesHoverItem = ({workspace, props: {nested}, data}: BuilderCardStore): boolean => {
    if (!this.config) return false

    const {catalog: {idFor}, id, catalogField, config: {itemField: swatchItemField}, card: {assortment: {heroCards: {item: itemConfig}}}, es} = this

    if (nested) {
      if (nested.field == catalogField) {
        return idFor(nested.data) === id
      } else {
        //debugger // NYI - does the colorway match this?
      }
    }

    let v = nested ? nested.parentData : data
    if (itemConfig?.field) {
      v = es.readField(data, itemConfig.field)?.val
    }

    v = _.isArray(v) ? _.flatMap(v, v1 => es.readField(v1, swatchItemField)?.val) : es.readField(v, swatchItemField)?.val

    // console.log({id, v})

    return _.isArray(v) ? v.some(x => x === id) : v === id
  }

  @computed get imageBackground() {
    const {card, card: {config: {imageBackgroundFor}}, stores} = this
    return _.isFunction(imageBackgroundFor) ? imageBackgroundFor('swatch', card, stores) : undefined
  }

  @computed get cssStyle() {
    const {imageBackground} = this

    return {
      '--catalog-image-background': imageBackground ? `url('${imageBackground}')` : undefined
    } as any
  }
}

export const AssortmentSwatchStoreContext = React.createContext<AssortmentSwatchStore>(null)
export const useAssortmentSwatchStore = () => React.useContext(AssortmentSwatchStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
  swatch: { field: string, item: any }
  index: number
}

export const AssortmentSwatch = observer<MyProps>(function AssortmentSwatch({className, index, swatch}) {
  const card = useAssortmentCardStore(),
    obsProps = useAsObservableSource({swatch, index, card}),
    store = useVipLocalStore((stores) => new AssortmentSwatchStore(stores, obsProps)),
    {catalog, image, hoverItemEvents, imageUrl, title, foreignKeyField, config, onContextMenu, onClick, onDoubleClick, isSelected, cssStyle} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <AssortmentSwatchStoreContext.Provider value={store}>
      <Tooltip title={config?.label ? config?.label : !foreignKeyField ? '' : catalog.labelForField(foreignKeyField)} placement='bottom'>
        <div className={classNames(css.root, className)}
             data-index={index}
             data-selected={isSelected ? true : undefined}
             data-field={swatch?.field}
             data-id={catalog.idFor(swatch)}
             data-type='AssortmentSwatch'
             onContextMenu={onContextMenu}
             onClick={onClick}
             onDoubleClick={onDoubleClick}
             {...hoverItemEvents}
        >
          <MyImage className={css.image}
                   imageUrl={imageUrl}
                   imageId={image?.exId}
                   lazy={false}
                   noImageContent={<div className={css.noImage}
                                        style={cssStyle}/>}
          />
          <span className={css.title}>{title}</span>
        </div>
      </Tooltip>
    </AssortmentSwatchStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/Assortments/AssortmentCard/Swatches/AssortmentSwatch.tsx